import {
  Badge,
  Banner,
  Button,
  Input,
  Loader,
  Select,
  Surface,
  Text,
  Textarea,
} from '@cloudflare/kumo'
import { ArrowLeft, Plus, Trash } from '@phosphor-icons/react'
import {
  EXPERT_REVIEW_DISCLAIMER,
  EXPERT_REVIEW_ITEM_LABELS,
  EXPERT_REVIEW_VERDICT_LABELS,
  EXPERT_REVIEW_VERDICTS,
  OPEN_EXPERT_REVIEW_STATUSES,
  type AdminExpertReviewView,
  type ExpertReviewAttachment,
  type ExpertReviewFinding,
  type ExpertReviewVerdict,
} from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ConfirmDialog } from '../components/confirm-dialog'
import { PageHeader } from '../components/page-header'
import { expertReviewsApi } from '../expert-reviews-api'
import { useGetToken } from '../lib/auth'
import { EXPERT_REVIEW_STATUS_LABEL } from '../lib/expert-review-labels'
import { fmtTime, toMessage } from '../lib/format'

const VERDICT_ITEMS = EXPERT_REVIEW_VERDICTS.map((v) => ({
  label: EXPERT_REVIEW_VERDICT_LABELS[v].ko,
  value: v,
}))
const MAX_ATTACHMENTS = 5

const isOpen = (r: AdminExpertReviewView) =>
  (OPEN_EXPERT_REVIEW_STATUSES as readonly string[]).includes(r.status)

/** 요청한 항목마다 빈 의견 하나. 저장된 의견이 있으면(끝난 요청) 그대로 */
const draftFindings = (r: AdminExpertReviewView): ExpertReviewFinding[] =>
  r.findings.length
    ? r.findings
    : r.items.map((item) => ({ item, verdict: 'ok' as const, comment: '' }))

/** 전문가 검수 1건 — 맡기, 항목별 의견·첨부를 써서 결과 보내기, 반려 */
export function ExpertReviewDetailPage() {
  const { id = '' } = useParams()
  const getToken = useGetToken()
  const navigate = useNavigate()
  const [review, setReview] = useState<AdminExpertReviewView | null>(null)
  const [findings, setFindings] = useState<ExpertReviewFinding[]>([])
  const [summary, setSummary] = useState('')
  const [attachments, setAttachments] = useState<ExpertReviewAttachment[]>([])
  const [rejectReason, setRejectReason] = useState('')
  const [confirm, setConfirm] = useState<'complete' | 'reject' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const show = useCallback((r: AdminExpertReviewView) => {
    setReview(r)
    setFindings(draftFindings(r))
    setSummary(r.summary)
    setAttachments(r.attachments)
  }, [])

  useEffect(() => {
    expertReviewsApi
      .get(getToken, id)
      .then(({ review: r }) => show(r))
      .catch((err: unknown) => setError(toMessage(err)))
  }, [getToken, id, show])

  const run = async (action: () => Promise<{ review: AdminExpertReviewView }>) => {
    setBusy(true)
    try {
      show((await action()).review)
      setError(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
      setConfirm(null)
    }
  }

  if (!review)
    return error ? (
      <Banner variant="error" title="문제가 생겼어요" description={error} />
    ) : (
      <div className="flex items-center gap-3 p-6">
        <Loader />
        <Text variant="secondary">불러오는 중…</Text>
      </div>
    )

  const editable = isOpen(review)
  const canSend =
    findings.every((f) => f.comment.trim()) &&
    attachments.every((a) => a.label.trim() && a.url.startsWith('https://'))
  const setFinding = (i: number, patch: Partial<ExpertReviewFinding>) =>
    setFindings(findings.map((f, j) => (j === i ? { ...f, ...patch } : f)))
  const setAttachment = (i: number, patch: Partial<ExpertReviewAttachment>) =>
    setAttachments(attachments.map((a, j) => (j === i ? { ...a, ...patch } : a)))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={review.productName || '이름 없는 제품'}
        description={`요청 ${fmtTime(review.createdAt)} · 기한 ${fmtTime(review.dueAt)}`}
        actions={
          <Button
            variant="secondary"
            icon={<ArrowLeft />}
            onClick={() => void navigate('/expert-reviews')}
          >
            대기열
          </Button>
        }
      />
      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <Surface className="flex flex-col gap-3 rounded-lg p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={review.status === 'rejected' ? 'error' : 'secondary'}>
            {EXPERT_REVIEW_STATUS_LABEL[review.status]}
          </Badge>
          {review.assigneeEmail && <Text variant="secondary">담당 {review.assigneeEmail}</Text>}
          {review.creditReturned && <Badge variant="secondary">검수권 반환</Badge>}
        </div>
        <Text>
          요청 항목: {review.items.map((i) => EXPERT_REVIEW_ITEM_LABELS[i].ko).join(', ')}
        </Text>
        <Text variant="secondary">사용자 메모: {review.note || '없음'}</Text>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              void navigate(`/simulations?open=${encodeURIComponent(review.simulationId)}`)
            }
          >
            결과 보기 (시뮬레이션)
          </Button>
          {review.status === 'requested' && (
            <Button
              loading={busy}
              onClick={() => void run(() => expertReviewsApi.start(getToken, review.id))}
            >
              맡기
            </Button>
          )}
        </div>
        <Text variant="secondary" size="xs">
          사용자에게 보이는 고지: {EXPERT_REVIEW_DISCLAIMER.ko}
        </Text>
      </Surface>

      <Surface className="flex flex-col gap-4 rounded-lg p-6">
        <Text variant="heading">항목별 의견</Text>
        {findings.map((f, i) => (
          <div key={f.item} className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <Text bold>{EXPERT_REVIEW_ITEM_LABELS[f.item].ko}</Text>
              <Select
                aria-label="판정"
                className="min-w-32"
                disabled={!editable}
                value={f.verdict}
                onValueChange={(v: string | null) =>
                  v && setFinding(i, { verdict: v as ExpertReviewVerdict })
                }
                items={VERDICT_ITEMS}
              />
            </div>
            <Textarea
              aria-label={`${EXPERT_REVIEW_ITEM_LABELS[f.item].ko} 의견`}
              disabled={!editable}
              value={f.comment}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFinding(i, { comment: e.target.value })
              }
            />
          </div>
        ))}
        <Textarea
          label="총평 (선택)"
          disabled={!editable}
          value={summary}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSummary(e.target.value)}
        />
        <div className="flex flex-col gap-2">
          <Text bold>첨부 (https 주소, 최대 {MAX_ATTACHMENTS}개)</Text>
          {attachments.map((a, i) => (
            <div key={i} className="flex items-end gap-2">
              <Input
                label="이름"
                disabled={!editable}
                value={a.label}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAttachment(i, { label: e.target.value })
                }
              />
              <Input
                label="주소"
                placeholder="https://"
                disabled={!editable}
                value={a.url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAttachment(i, { url: e.target.value.trim() })
                }
              />
              {editable && (
                <Button
                  variant="ghost"
                  shape="square"
                  icon={<Trash />}
                  aria-label="첨부 삭제"
                  onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}
                />
              )}
            </div>
          ))}
          {editable && attachments.length < MAX_ATTACHMENTS && (
            <div>
              <Button
                variant="ghost"
                size="sm"
                icon={<Plus />}
                onClick={() => setAttachments([...attachments, { label: '', url: '' }])}
              >
                첨부 추가
              </Button>
            </div>
          )}
        </div>
        {editable && (
          <div>
            <Button disabled={!canSend} loading={busy} onClick={() => setConfirm('complete')}>
              결과 보내기
            </Button>
          </div>
        )}
      </Surface>

      {editable && (
        <Surface className="flex flex-col gap-3 rounded-lg p-6">
          <Text variant="heading">반려</Text>
          <Textarea
            label="반려 사유 (사용자에게 보여요)"
            value={rejectReason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setRejectReason(e.target.value)
            }
          />
          <div>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim()}
              loading={busy}
              onClick={() => setConfirm('reject')}
            >
              반려하고 검수권 돌려주기
            </Button>
          </div>
        </Surface>
      )}

      <ConfirmDialog
        open={confirm === 'complete'}
        title="검수 결과를 보낼까요?"
        description="사용자 앱에 그대로 보이고 알림이 가요. 보낸 뒤에는 고칠 수 없어요."
        confirmLabel="결과 보내기"
        busy={busy}
        onConfirm={() =>
          void run(() =>
            expertReviewsApi.complete(getToken, review.id, { findings, summary, attachments }),
          )
        }
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'reject'}
        title="이 요청을 반려할까요?"
        description="쓴 검수권이 사용자에게 돌아가고 사유와 함께 알림이 가요. 되돌릴 수 없어요."
        confirmLabel="반려"
        destructive
        busy={busy}
        onConfirm={() => void run(() => expertReviewsApi.reject(getToken, review.id, rejectReason))}
        onClose={() => setConfirm(null)}
      />
    </div>
  )
}
